import type { FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { PasswordRulesHint } from 'components/PasswordRulesHint';
import { addNotification } from 'notifications';
import { Button, TextField } from 'ui';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { resetPasswordSchema } from './schema';
import { useResetConfirmMutation } from './services';
import classes from '../Registration/register.module.scss';

import { Form, Formik } from 'formik';

export const ResetPassword: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Достаем токен из URL: /reset-password?token=какой_то_jwt
  const token = searchParams.get('token');
  const resetConfirm = useResetConfirmMutation();

  return (
    <section className={classes.wrapper}>
      <main className={classes.container}>
        <Formik
          initialValues={{ password: '', confirmPassword: '' }}
          validationSchema={resetPasswordSchema}
          onSubmit={async (values, helpers) => {
            if (!token) {
              addNotification(
                'Токен восстановления не найден. Перейдите по ссылке из письма еще раз.',
                'error',
              );
              return;
            }

            try {
              // Собираем payload строго по доке бэка
              await resetConfirm.mutateAsync({
                token: token,
                new_password: values.password,
                repeat_new_password: values.confirmPassword,
              });

              // Если ок - переходим на экран успеха
              navigate('/reset-success');
            } catch (error) {
              // Ошибки про сам пароль («должен отличаться», длина, совпадение) показываем
              // под полем (TextField выводит их плашкой .fig-field-error), остальные
              // (например, просроченная ссылка) - всплывашкой.
              const message = getApiErrorMessage(error, 'Ошибка восстановления пароля');
              const lower = message.toLowerCase();
              const field = lower.includes('не совпадают')
                ? 'confirmPassword'
                : lower.includes('отличаться') || lower.includes('от 8 до 128')
                  ? 'password'
                  : null;
              if (field) {
                helpers.setFieldTouched(field, true, false);
                helpers.setFieldError(field, message);
                return;
              }
              addNotification(message, 'error');
            }
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, handleBlur, setFieldValue }) => (
            <Form className={classes.form}>
              {/* Figma 21:7697: заголовок, подзаголовок, поля с подписями, кнопка. */}
              <div className={classes.heading}>
                <h1 className={classes.title}>Новый пароль</h1>
                <p className={classes.subtitle}>Задайте новый пароль для аккаунта.</p>
              </div>

              <div className={classes.fieldGroup}>
                <TextField
                  className='fig-auth-field'
                  shape='brick'
                  label='Новый пароль'
                  placeholder='Новый пароль'
                  size='lg'
                  type='password'
                  name='password'
                  value={values.password}
                  onChange={(val) => setFieldValue('password', val)}
                  onBlur={handleBlur}
                  isInvalid={touched.password && errors.password}
                  disabled={resetConfirm.isPending}
                />
                <PasswordRulesHint />

                <TextField
                  className='fig-auth-field'
                  shape='brick'
                  label='Повторите пароль'
                  placeholder='Повторите пароль'
                  size='lg'
                  type='password'
                  name='confirmPassword'
                  value={values.confirmPassword}
                  onChange={(val) => setFieldValue('confirmPassword', val)}
                  onBlur={handleBlur}
                  isInvalid={touched.confirmPassword && errors.confirmPassword}
                  disabled={resetConfirm.isPending}
                />
              </div>

              <Button
                type='submit'
                variant='filled'
                shape='brick'
                widthFullSize
                loading={resetConfirm.isPending}
                disabled={resetConfirm.isPending}
                className={classes.submitButton}
              >
                Сохранить пароль
              </Button>
            </Form>
          )}
        </Formik>
      </main>
    </section>
  );
};
